import React, {useState} from 'react';

import { useForm, Controller } from "react-hook-form";
import type {Control, FieldErrors, RegisterOptions} from "react-hook-form";

import Form from 'react-bootstrap/Form';

import {Checkbox, TextField} from "@mui/material";
import {CheckBox, CheckBoxOutlineBlank} from "@vedmarket/ui-kit";

import styles from './ContactForm.module.scss';
import {COLORS} from "../../../../../../config/constants";
import ContainedButton from "../../../components/buttons/Contained";
import List1 from "../../../components/typography/List1";
import {typografText} from "../../../utils/typograf";

const TEXT_FIELD_VARIANT = 'outlined';
const FORM_TITLE = 'Заявка с сайта';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FIELD_NAMES = {
    title: 'DATA.TITLE',
    name: 'DATA.NAME',
    email: 'DATA.EMAIL',
    comments: 'DATA.COMMENTS',
} as const;

const FIELD_ERRORS = {
    name: 'NAME',
    email: 'EMAIL',
    comments: 'COMMENTS',
} as const;

type ContactFormData = {
    DATA: {
        TITLE: string;
        NAME: string;
        EMAIL: string;
        COMMENTS: string;
    };
};

type ContactFormProps = {
    onSuccess: (isSuccess: boolean) => void;
    onError: (isError: boolean) => void;
};

type ContactFieldName = typeof FIELD_NAMES.name | typeof FIELD_NAMES.email | typeof FIELD_NAMES.comments;

type ContactTextFieldProps = {
    control: Control<ContactFormData>;
    className?: string;
    errors: FieldErrors<ContactFormData>;
    name: ContactFieldName;
    errorKey: keyof ContactFormData['DATA'];
    label: string;
    rules?: RegisterOptions<ContactFormData, ContactFieldName>;
    multiline?: boolean;
    rows?: number;
};

const textFieldStyles = {
    '& .MuiInputLabel-root': {
        backgroundColor: COLORS.WHITE,
        color: '#666666',
        fontSize: '14px',
        lineHeight: 1.44,
        padding: '0 5px',
        transform: 'translate(14px, -9px) scale(0.75)',
        '&.Mui-focused, &.Mui-error': {
            color: '#666666',
        },
    },
    // Match the shared FormControl selectors so focus/error outlines stay visible.
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
        backgroundColor: COLORS.WHITE,
        border: 0,
        '&.Mui-focused, &.Mui-error': {
            border: 0,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${COLORS.GRAY_BORDER_LIGHT}`,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${COLORS.PRIMARY}`,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${COLORS.ERROR_LIGHT}`,
        },
    },
    '@media (max-width: 767px)': {
        '& .MuiOutlinedInput-notchedOutline legend': {
            fontSize: '10.5px',
        },
    },
    '& .MuiOutlinedInput-input': {
        color: COLORS.BLACK,
        fontSize: '14px',
        letterSpacing: '0.15px',
        lineHeight: 1.71,
        padding: '12px',
        '@media (max-width: 767px)': {
            padding: '8px 12px',
        },
    },
    '& .MuiInputBase-root:not(.MuiInputBase-multiline)': {
        '@media (min-width: 768px)': {
            height: '48px',
        },
        '@media (min-width: 576px) and (max-width: 767px)': {
            height: '48px',
        },
    },
    '& .MuiInputBase-root.MuiInputBase-multiline': {
        minHeight: '96px',
        '@media (min-width: 1200px)': {
            minHeight: '96px',
        },
        '@media (min-width: 768px) and (max-width: 1199px)': {
            minHeight: '96px',
        },
    },
    '@media (min-width: 576px) and (max-width: 767px)': {
        '& .MuiInputLabel-root': {
            fontSize: '16px',
        },
        '& .MuiOutlinedInput-input': {
            fontSize: '16px',
            lineHeight: 1.5,
            letterSpacing: 0,
            padding: '12px',
        },
        '& .MuiInputBase-root.MuiInputBase-multiline': {
            padding: '12px',
            minHeight: '96px',
        },
        '& .MuiInputBase-inputMultiline': {
            padding: 0,
            lineHeight: 1.5,
        },
    },
    '@media (min-width: 768px)': {
        '& .MuiInputLabel-root': {
            fontSize: '16px',
        },
        '& .MuiOutlinedInput-input': {
            fontSize: '16px',
            lineHeight: 1.5,
            letterSpacing: 0,
        },
        '& .MuiInputBase-root.MuiInputBase-multiline': {
            padding: '12px',
        },
        '& .MuiInputBase-inputMultiline': {
            padding: 0,
        },
    },
    '@media (max-width: 575px)': {
        '& .MuiInputBase-root:not(.MuiInputBase-multiline)': {
            height: 'max-content',
        },
        '& .MuiInputLabel-root': {
            fontSize: '16px',
            color: COLORS.GRAY_LIGHT_SECONDARY,
        },
        '& .MuiOutlinedInput-input': {
            fontSize: '16px',
            padding: '12px',
            letterSpacing: 0,
            lineHeight: 1.5,
            height: '1.5em',
        },
        '& .MuiInputBase-root.MuiInputBase-multiline': {
            minHeight: 0,
            padding: '12px',
        },
        '& .MuiInputBase-inputMultiline': {
            padding: 0,
            lineHeight: 1.5,
        },
    },
};

function createRequestBody(data: ContactFormData) {
    const formData = new URLSearchParams();

    Object.entries(data.DATA).forEach(([key, value]) => {
        formData.append(`DATA[${key}]`, value);
    });

    return formData.toString();
}

function ContactTextField({control, className, errors, name, errorKey, label, rules, multiline, rows}: ContactTextFieldProps) {

    const error = errors.DATA?.[errorKey];

    return (
        <Controller
            name={name}
            control={control}
            defaultValue=""
            rules={rules}
            render={({ field }) => (
                <TextField
                    {...field}
                    className={className}
                    label={typografText(label)}
                    variant={TEXT_FIELD_VARIANT}
                    error={!!error}
                    helperText={typeof error?.message === 'string' ? typografText(error.message) : error?.message}
                    fullWidth
                    multiline={multiline}
                    rows={rows}
                    InputLabelProps={{shrink: true}}
                    sx={textFieldStyles}
                />
            )}
        />
    );
}

function ContactForm({onSuccess, onError}: ContactFormProps) {

    const [isSending, setIsSending] = useState(false);
    const [hasConsent, setHasConsent] = useState(true);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<ContactFormData>({
        mode: "onBlur",
    });

    const onSubmit = async (data: ContactFormData) => {
        if (!hasConsent || isSending) {
            return;
        }

        setIsSending(true);

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: createRequestBody(data)
            });

            if (response.ok) {
                onSuccess(true);
                onError(false);
                reset();
            } else {
                onError(true);
                onSuccess(false);
            }
        } catch (error) {
            onError(true);
            onSuccess(false);
        } finally {
            setIsSending(false)
        }

    };

    return (
        <Form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <Controller
                name={FIELD_NAMES.title}
                control={control}
                defaultValue={FORM_TITLE}
                render={({ field }) => (
                    <TextField
                        {...field}
                        hidden
                    />
                )}
            />
            <ContactTextField
                name={FIELD_NAMES.name}
                errorKey={FIELD_ERRORS.name}
                label="First name"
                control={control}
                errors={errors}
                rules={{
                    required: "This field is required",
                }}
            />
            <ContactTextField
                className={styles.emailField}
                name={FIELD_NAMES.email}
                errorKey={FIELD_ERRORS.email}
                label="Email"
                control={control}
                errors={errors}
                rules={{
                    required: "This field is required",
                    pattern: {
                        value: EMAIL_PATTERN,
                        message: "Invalid email address.",
                    },
                }}
            />
            <ContactTextField
                className={styles.commentsField}
                name={FIELD_NAMES.comments}
                errorKey={FIELD_ERRORS.comments}
                label="Message (optional)"
                control={control}
                errors={errors}
                multiline
                rows={3}
            />
            <label className={styles.agreement}>
                <Checkbox
                    required
                    checkedIcon={<CheckBox/>}
                    className={styles.agreementCheckbox}
                    checked={hasConsent}
                    onChange={(_, checked) => setHasConsent(checked)}
                    icon={<CheckBoxOutlineBlank/>}
                    size="small"
                    sx={{
                        '@media (min-width: 576px) and (max-width: 991px)': {
                            padding: 0,
                            width: '24px',
                            height: '24px',
                        },
                        '@media (max-width: 575px)': {
                            padding: 0,
                            '& .MuiSvgIcon-root': {
                                fontSize: '24px',
                            },
                        },
                    }}
                />
                <List1 className={styles.agreementText} sx={{
                    color: COLORS.BLACK,
                }}>
                    I consent to the processing of <a className={styles.agreementLink} href="/agreement">my personal data</a>
                </List1>
            </label>
            <ContainedButton
                icon="send"
                isBig
                type="submit"
                disabled={isSending || !hasConsent}
                className={styles.submitButton}
                compactDesktop
            >
                Submit
            </ContainedButton>
        </Form>
    );
}

export default ContactForm;
